import { useEffect, useState } from 'react'

/**
 * Sequences mount/open state for transition-based overlays (MUI Drawer/Modal/Dialog).
 *
 * MUI Drawer suppresses `appear` on mount, so mounting it with `open=true` shows
 * instantly without a slide-in; unmounting on close kills the exit transition.
 * This hook keeps the component mounted through the exit transition and flips
 * `open` only after it has mounted.
 *
 * ```tsx
 * const drawer = useTransitionMount(openJADrawer)
 * return drawer.mounted ? <JADetailsDrawer open={drawer.open} setOpen={setOpenJADrawer} /> : null
 * ```
 *
 * @param open Caller intent — true while the overlay should be visible.
 * @param exitDurationMs How long to stay mounted after `open` goes false. Must
 *   outlast the exit transition (MUI default leavingScreen ≈ 195ms).
 */
export function useTransitionMount(open: boolean, exitDurationMs = 350) {
    const [mounted, setMounted] = useState(false)
    const [transitionOpen, setTransitionOpen] = useState(false)

    useEffect(() => {
        if (open) {
            setMounted(true)
            return
        }
        setTransitionOpen(false)
        const timer = setTimeout(() => setMounted(false), exitDurationMs)
        return () => clearTimeout(timer)
    }, [open, exitDurationMs])

    // Mount first (open=false), flip open on the next commit so the real
    // enter transition runs. Also fires on reopen within the exit window.
    useEffect(() => {
        if (mounted && open) setTransitionOpen(true)
    }, [mounted, open])

    return { mounted, open: transitionOpen }
}
